import React, { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { useAiChatServices } from '../AiChatContext';
import { extractLinkAction } from '@lib/utils/urlParser';
import Prompt from './Prompt';
import { PromptV2 } from '../../v2/PromptV2';
import Message from './Message';
import { type Interaction, type MergeResult } from '@lib/models/Interaction';
import StickyQuestionHeader from './StickyQuestionHeader';
import AlertDialog from './AlertDialog';
// Mobile responsive imports
import { useTheme, useMediaQuery, Fade, Tooltip, Divider, LinearProgress, alpha } from '@mui/material';
import CurrentMessage from './CurrentMessage';
import AskUserSheet from './AskUserSheet';
import _ from 'lodash';
import { LockOpen, Lock } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useAiChatSessionStore } from '../AiChatContext';
import { getServerBaseUrl } from '@lib/utils/environmentUtils';
import SortableTable from '@lib/utils/sortable-table';
import ConnectionState from './ConnectionState';
import { ModelConfig } from '@lib/models/enums';
import { InertEnvironmentProvider } from '@lib/components';

interface ChatPaneProps {
    isAppActive?: boolean;
    showStickHeader?: boolean;
}

const serverUrlBase = getServerBaseUrl();

type ModelConfigEntry = (typeof ModelConfig)[keyof typeof ModelConfig];

const ChatPane: React.FC<ChatPaneProps> = (props) => {
    const animationTime = 4000;
    const { eventBusService, showStickyHeader, currentModel, friendlyMode, compactPrompt, showConnectionState, hidePromptInput } = useAiChatServices();
    const currentModelConfig = currentModel ? (ModelConfig as Record<string, ModelConfigEntry>)[currentModel] : undefined;
    const sessionId = useAiChatSessionStore((state) => state.session?.id);
    const isTurnOpen = useAiChatSessionStore((state) => state.turnOpen);
    const connectionState = useAiChatSessionStore((state) => state.connectionStatus);
    const messages = useAiChatSessionStore((state) => state.messages);
    const loadingType = useAiChatSessionStore((state) => state.loadingType);
    const setLastActivity = useAiChatSessionStore((state) => state.setLastActivity);
    const lastTimeout = useRef<NodeJS.Timeout | null>(null);
    // Mobile responsive detection
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // xs, sm
    const isTouchDevice = isMobile;
    
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const chatContainerRef = useRef<HTMLDivElement | null>(null);
    const didInitialScrollRef = useRef(false);

    // Sticky header state
    const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const [forceMessage, setForceMessage] = useState<string | null>(null);
    const { artifactService } = useAiChatServices();

    // Scroll detection and new content indicator state
    const [showNewContentIndicator, setShowNewContentIndicator] = useState(false);
    const [showScrollLock, setShowScrollLock] = useState(false);
    const [startVanish, setStartVanish] = useState(false);
    const [forceToolTip, setForceTooltip] = useState(false);
    const [dashboardUrl, setDashboardUrl] = useState<string | null>(null);
    const [isMouseOverLock, setIsMouseOverLock] = useState(false);
    const isScrollLockedRef = useRef(false);
    const lastScrollToBottom = useRef(0);
    const lastScrollPosition = useRef(0);
    const [promptHeight, setPromptHeight] = useState(0)
    // const isContainerScrollableRef = useRef(false);

    // Alert dialog state
    const [alertDialog, setAlertDialog] = useState<{
        open: boolean;
        title: string;
        message: string;
        severity: 'info' | 'warning' | 'error';
        actionType: 'retry' | 'close';
    }>({
        open: false,
        title: '',
        message: '',
        actionType: 'retry',
        severity: 'info'
    });

    const showAlert = useCallback((title: string, message: string, severity: 'info' | 'warning' | 'error' = 'info', actionType: 'retry' | 'close' = 'retry') => {
        setAlertDialog({
            open: true,
            title,
            message,
            severity,
            actionType
        });
    }, []);

    const closeAlertAndRetry = useCallback(() => {
        setAlertDialog(prev => ({ ...prev, open: false }));
    }, []);

    const onTurnComplete = useCallback(async (_interactionId: string, _agentInteraction: Interaction) => {
        handleUnlock(false);
        setShowNewContentIndicator(false);
    }, [chatContainerRef]);

    const onTurnOpen = async (_interactionId: string) => {
        // Intentionally a no-op: engaging the scroll lock here makes
        // the MutationObserver continuously snap back to the bottom
        // during streaming and fights the user's attempts to scroll
        // up to read older content (the onWheel/onScrollEnd unlock
        // path is too slow to win that race). Auto-follow during a
        // turn is opt-in: it engages 200ms after `chatOnMessageSent`
        // (when the user explicitly sent the turn) or when the user
        // clicks the "New content!" pill.
    }

    useEffect(() => {
        eventBusService.gatewayOnTurnComplete(onTurnComplete);
        eventBusService.gatewayOnTurnOpen(onTurnOpen);
        eventBusService.gatewayOnMessage(handleNewMessage);
        eventBusService.chatOnMessageSent(handleMessageSent)
        eventBusService.chatOnMessageError(onMessageError)
        return () => {
            eventBusService.gatewayOffTurnComplete(onTurnComplete);
            eventBusService.gatewayOffTurnOpen(onTurnOpen);
            eventBusService.gatewayOffMessage(handleNewMessage);
            eventBusService.chatOffMessageSent(handleMessageSent)
            eventBusService.chatOffMessageError(onMessageError)

        }
    }, [eventBusService]);

    useEffect(() => {
        const clear = () => {
            messageRefs.current.clear();
        }

        const sortableTable = new SortableTable({ theme: theme });
        if (chatContainerRef) {
            setTimeout(() => {
                sortableTable.init(chatContainerRef.current)
            }, 500);
        }

        clear();
        // Scroll to bottom after session content is loaded.
        // First time messages become available we always snap to the
        // latest entry (so opening a session lands on the current
        // message, not the top of a long transcript). After that, we
        // only auto-scroll on message changes when the user is in
        // "follow streaming" mode (lock engaged).
        setTimeout(() => {
            const hasMessages = (messages?.length ?? 0) > 0;
            if (!didInitialScrollRef.current && hasMessages) {
                didInitialScrollRef.current = true;
                scrollToBottom(true); // immediate scroll to bottom
                return;
            }
            if (isScrollLockedRef.current)
                scrollToBottom(true);
        }, 100);

        return () => {
            sortableTable.destroy();
        }

    }, [messages]);

    const observerRef = useRef<() => void>(() => { });
    observerRef.current = () => {
        const container = chatContainerRef.current;
        if (!container || !isTurnOpen) return;

        if (container.scrollTop < 0) return;

        lastScrollPosition.current = container.scrollTop;

        const discount = container.clientHeight * 0.15;
        if (container.scrollTop + container.clientHeight < container.scrollHeight - discount) {
            if (isScrollLockedRef.current) {
                scrollToBottom();
            }
            else {
                if (!showNewContentIndicator)
                    setShowNewContentIndicator(() => true);
            }
        }
        else {
            if (showNewContentIndicator)
                setShowNewContentIndicator(() => false);
        }
    };

    // Add global mouse event listeners
    useEffect(() => {
        const handleGlobalMouseMove = (e: MouseEvent) => {
            if (lastTimeout?.current)
                clearTimeout(lastTimeout.current)

            lastTimeout.current = setTimeout(() => {

                setLastActivity()
                lastTimeout.current = null;
            }, 50)
        };

        const handleGlobalMouseUp = () => {
            setLastActivity()
        }
        // Always add listeners to main document
        document.addEventListener("mousemove", handleGlobalMouseMove);
        window.addEventListener("mouseup", handleGlobalMouseUp);

        const handleKeyDown = (_event: KeyboardEvent) => {
            setLastActivity()
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener("mousemove", handleGlobalMouseMove);
            window.removeEventListener("mouseup", handleGlobalMouseUp);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [setLastActivity]);

    useEffect(() => {
        const container = chatContainerRef.current;
        if (!container) return;

        const mutationObserver = new MutationObserver(() => observerRef.current());

        mutationObserver.observe(container, {
            childList: true,
            subtree: true,
            characterData: true
        });

        return () => {
            mutationObserver.disconnect();
        }
    }, [chatContainerRef.current])

    const askQuestion = async (question: string) => {
        setForceMessage(question);
    };

    /**
     * Triggers a file download without navigating away from the page.
     * Creates a temporary anchor with the download attribute and programmatically clicks it.
     */
    const triggerDownload = useCallback((url: string) => {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = url;
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    }, []);

    const handleLinkAction = useCallback(async (link: string, content: string | null) => {
        const { action, url, question, parameter } = extractLinkAction(link, sessionId ?? '', content);
        switch (action) {
            case 'URL':
                window.open(url!, '_blank');
                break;
            case 'OpenDashboard':
                setDashboardUrl(parameter!);
                break;
            case 'Export':
                if (link.indexOf("http") == -1) {
                    link = serverUrlBase + link;
                }
                triggerDownload(link);
                break;
            case 'Question':

                await askQuestion(question!);
        }

    }, [messages, triggerDownload, dashboardUrl]);

    const scrollToBottom = useCallback((immediate: boolean = false) => {
        if (!chatContainerRef.current) return;

        const diff = new Date().getTime() - lastScrollToBottom.current;
        if (diff > 300) {
            const container = chatContainerRef.current;

            // Perform initial scroll
            container.scrollTo({
                top: container.scrollHeight,
                behavior: immediate ? 'instant' : 'smooth'
            });

            lastScrollToBottom.current = new Date().getTime();
        }

    }, []);

    function handleScrollEnd() {
        const currentPosition = chatContainerRef.current?.scrollTop ?? 0;
        const lastPosition = lastScrollPosition.current;

        if (currentPosition < lastPosition && showScrollLock) {
            handleUnlock(false);
        }
    }

    const scrollALittleUp = useCallback(() => {
        if (chatContainerRef.current && !isTouchDevice) {
            chatContainerRef.current.scrollBy({
                top: chatContainerRef.current.scrollHeight * 0.1 * -1,
                behavior: 'instant',
            })
        }
    }, [chatContainerRef.current, chatContainerRef.current?.scrollHeight]);

    const handleTouchMove = () => {

        handleUnlock(false);
    };

    const handleUnlock = (enableAnimation?: boolean) => {
        if (enableAnimation) {
            isScrollLockedRef.current = false;
            scrollALittleUp();
        }
        else {
            isScrollLockedRef.current = false;
        }

        setStartVanish(true);
        setTimeout(() => {
            setShowScrollLock(() => false);
        }, animationTime)
    }

    const handleLock = () => {
        scrollToBottom();
        isScrollLockedRef.current = true;
        setShowScrollLock(() => true);
        setShowNewContentIndicator(false);

        setStartVanish(() => false);
    }

    const getResponsiveChatContainerStyles = useCallback(() => {
        let style = { //default to medium to xl
            display: 'flex',
            pl: isMobile ? 1 : 2,
            pt: 0,
            pr: isMobile ? 1 : 0.5,
            m: 0,
            ml: 0,
            scrollbarWidth: '5px',
            backgroundColor: theme.palette?.surface?.raised ?? theme.palette.background.paper,
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            border: `0px`,
            transition: 'border-color 0.5s ease',
            position: 'relative'
        }

        return style;
    }, [isMobile, theme]);



    const onMessageError = useCallback((error: string) => {
        showAlert('Send message', `**Error while trying to send the message:** ***${error}***\n. Tente novamente.`, 'error', 'close')
        return Promise.resolve();
    }, []);

    const handleMessageSent = useCallback((_message: string, _interaction: Interaction) => {
        setTimeout(() => handleLock(), 200);
        return Promise.resolve();
    }, []);

    const handleNewMessage = async (_interaction: Interaction, _mergeDetails: MergeResult | null) => {
        if (!chatContainerRef.current) return;

        if (isScrollLockedRef.current)
            handleLock();
    }

    return (
        <Box sx={getResponsiveChatContainerStyles()}>
            {props.showStickHeader && (<StickyQuestionHeader
                messageRefs={messageRefs}
                showStickyHeader={showStickyHeader}
            />)}
            {showConnectionState && (
                <div style={{ position: "absolute", right: '0px', top: '5px', zIndex: 10000 }}>
                    <ConnectionState />
                </div>
            )}
            {(loadingType !== undefined && connectionState != 'Idle' && connectionState != 'Connected') && (
                <div style={{ display: 'inline-flex', position: "absolute", backgroundColor: alpha(theme.palette.background.default,0.6), justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', right: '0px', top: '0', zIndex: 10000 }}>
                    <Box sx={{
                        width: '100%',
                        height:'100%',
                        alignContent:'end',
                        '& .MuiLinearProgress-root': {
                            backgroundColor: 'rgba(0, 0, 0, 0.15) !important'
                        }
                    }}>
                        <LinearProgress
                            variant={'indeterminate'}
                            sx={(theme) => ({
                                height: 5,
                                borderRadius: 0,
                                '& .MuiLinearProgress-bar1': {
                                    backgroundColor: theme.palette.primary.dark,
                                    opacity: 0.8
                                },
                                '& .MuiLinearProgress-bar2': {
                                    backgroundColor: theme.palette.primary.dark,
                                    opacity: 0.8
                                }
                            })}
                        />
                    </Box>

                </div>)}

            <Box
                ref={(el) => {
                    if (el) {
                        chatContainerRef.current = el as HTMLDivElement;
                    }
                }}
                id={`chat-container-${sessionId ?? 'new'}`}
                onTouchMove={() => handleTouchMove()}
                // React's synthetic-event system doesn't recognise the DOM
                // `scrollend` event yet (would warn at runtime). The `onWheel`
                // handler below covers the same intent for desktop scroll;
                // touch scrollers fire `onTouchMove`. If a real `scrollend`
                // listener is ever needed, attach it via a `useEffect` +
                // `chatContainerRef.current.addEventListener('scrollend', …)`.
                onWheel={() => handleScrollEnd()}
                sx={{
                    '&::-webkit-scrollbar': {
                        width: isMobile ? 'none' : '4px',
                        height: isMobile ? 'none' : '4px',
                        background: theme.palette.background.default,
                    },
                    '&::-webkit-scrollbar-track': {
                        background: theme.palette.background.default,
                        width: isMobile ? 'none' : '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        // Hidden (blends into the track) while the scroll is locked, otherwise the primary handle.
                        background: showScrollLock ? theme.palette.background.default : theme.palette.primary.dark,
                        border: '1px solid',
                        borderColor: showScrollLock ? theme.palette.background.default : theme.palette.border.default,
                        borderRadius: '4px',
                        '&:hover': {
                            background: theme.palette.primary.main
                        },
                    },
                    flex: 1, overflowY: 'auto', overflowX: 'hidden', p: 0, scrollbarWidth: isMobile ? 'none' : '5px', scrollbarColor: 'rgba(0, 0, 0, 0.06)', position: 'relative',
                    maxWidth: '100%',
                    // Canonical "scrollable child of a flex column"
                    // recipe: minHeight: 0 lets the flex layout shrink
                    // this past its content's intrinsic height, which
                    // is what makes overflowY: auto actually engage.
                    // The previous `height: '100%'` fought flex: 1 and
                    // let the container grow past the available space,
                    // pushing the Prompt off-screen and turning the
                    // outer wrapper into the de-facto scroller.
                    minHeight: 0,
                }}
            >
                {messages !== null &&
                    // Historical messages live inside `InertEnvironmentProvider`
                    // so any live-TSX side-effect hooks they carry (e.g.
                    // an LLM-emitted `useDashboardPad`, `highlightComponent`,
                    // `cloneComponent`, …) become no-ops when the chat
                    // re-mounts on session reload. The side effects already
                    // fired during the live stream — the agent's dashboard
                    // pad was registered while `<CurrentMessage>` was alive
                    // and is the DB's source of truth — so replaying the
                    // history must be visual-only. Without this wrapper the
                    // replay's `useEffect`s clobbered live state every time
                    // the user opened the session (the "h:1 corruption"
                    // path).
                    <InertEnvironmentProvider>
                        <Stack direction="column" spacing={0}>
                            {messages.filter((m) => m.turn_complete).map((message, index) => (
                                <Box
                                    key={message.interactionId}
                                    sx={{ p: 0.5, width: isMobile ? '100%' : '98%' }}
                                >
                                    <Message
                                        messagesRef={messageRefs}
                                        onLinkClicked={handleLinkAction}
                                        message={message}
                                        appendWaitingPart={false}
                                        friendlyMode={friendlyMode}
                                    />
                                    {(index < messages.length - 2) && <Divider sx={{ mt: 0, mb: 2 }} />}
                                </Box>
                            ))}
                        </Stack>
                    </InertEnvironmentProvider>
                }

                {/* `<CurrentMessage>` stays OUTSIDE the inert wrapper
                    so the streaming turn keeps the real environment —
                    live-TSX blocks emitted during the active turn
                    (`useDashboardPad`, etc.) must be able to register
                    their dashboard pad with the composer store. The
                    moment the turn closes `currentMessage` becomes
                    null and this slot unmounts; the same content
                    moves into `messages` and renders under the inert
                    wrapper from then on. */}
                <CurrentMessage
                    artifactService={artifactService}
                    eventBusService={eventBusService}
                    onLinkClicked={handleLinkAction}
                    messagesRef={messageRefs}
                    friendlyMode={friendlyMode}
                />
                {/* <LlmResponseRender content={declaration} sessionId={''} partial={false} onLinkClicked={function (link: string, content: string | null): void {
                    throw new Error('Function not implemented.');
                }}></LlmResponseRender> */}

                <div ref={messagesEndRef}></div>
            </Box>
            {/* Model Watermark */}
            {currentModelConfig && (
                <Typography
                    variant="caption"
                    sx={{
                        position: 'absolute',
                        bottom: 120,
                        right: 12,
                        opacity: 0.18,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        pointerEvents: 'none',
                        userSelect: 'none',
                        zIndex: 0,
                        color: 'text.primary',
                    }}
                >
                    {currentModelConfig.name}
                </Typography>
            )}
            {/* New Content Indicator - positioned relative to the main chat pane */}
            <Fade in={showNewContentIndicator} timeout={300}>
                <Box
                    sx={{

                        position: 'absolute',
                        bottom: promptHeight + 30, // Position above the prompt area
                        right: 15,
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        borderRadius: 2,
                        px: 1,
                        py: 1,
                        boxShadow: 3,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        zIndex: 1000,
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                            '0%': {
                                transform: 'scale(1)',
                                opacity: 1,
                            },
                            '50%': {
                                transform: 'scale(1.05)',
                                opacity: 0.8,
                            },
                            '100%': {
                                transform: 'scale(1)',
                                opacity: 1,
                            },
                        },
                        '&:hover': {
                            backgroundColor: theme.palette.secondary.dark,
                            transform: 'scale(1.02)',
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: -20,
                            right: 115,
                            width: 16,
                            height: 20,
                            backgroundColor: theme.palette.text.secondary,
                            clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                            borderRadius: '0 0 0 0',
                        }
                    }}
                    onClick={() => {
                        handleLock();
                    }}
                >
                    {isScrollLockedRef.current === true ? <LockOpen sx={{ fontSize: 10 }} /> : <Lock sx={{ fontSize: 20 }} />}
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        New content!
                    </Typography>
                    <KeyboardArrowDownIcon sx={{ fontSize: 15 }} />
                </Box>
            </Fade>

            {
                (showScrollLock) && <Tooltip title="Clique para desativar o acompanhamento de escrita" arrow={true} placement='right'
                    slotProps={{
                        tooltip: { sx: { backgroundColor: 'secondary.light', color: "black" } }
                    }}
                >
                    <Box
                        onClick={() => handleUnlock()}
                        sx={{
                            position: 'absolute',
                            bottom: promptHeight + 10, // Position above the prompt area
                            right: 12,
                            backgroundColor: theme.palette.secondary.light,
                            color: theme.palette.getContrastText(theme.palette.secondary.light),
                            borderColor: theme.palette.divider,
                            borderRadius: 2,
                            width: '20px',
                            paddingLeft: '0.5px',
                            height: '45px',
                            alignContent: 'center',
                            boxShadow: 3,
                            cursor: 'pointer',
                            alignItems: 'center',
                            zIndex: 1000,
                            textAlign: 'center',
                            animation: !startVanish ? 'pulse 2s infinite' : `vanish ${animationTime / 1000}s ease-out`,
                            '@keyframes vanish': startVanish ? {
                                '0%': {
                                    transform: 'scale(1.5)',
                                    opacity: 1,
                                },
                                '25%': {
                                    transform: 'scale(1.125)',
                                    opacity: 0.7,
                                },
                                '50%': {
                                    transform: 'scale(0.750)',
                                    opacity: 0.4,
                                },
                                '75%': {
                                    transform: 'scale(0.375)',
                                    opacity: 0.1,
                                },
                                '100%': {
                                    transform: 'scale(0)',
                                    opacity: 0,
                                },
                            } : {},
                            '@keyframes pulse': !startVanish ? {
                                '0%': {
                                    transform: 'scale(1)',
                                    opacity: 1,
                                },
                                '50%': {
                                    transform: 'scale(1.05)',
                                    opacity: 0.8,
                                },
                                '100%': {
                                    transform: 'scale(1)',
                                    opacity: 1,
                                },
                            } : {},
                            '&:hover': {
                                backgroundColor: theme.palette.secondary.main,
                                transform: 'scale(1.02)',
                            },
                        }}
                        onMouseOver={() => {
                            setIsMouseOverLock(() => true)
                        }}
                        onMouseLeave={() => {
                            setIsMouseOverLock(() => false)
                        }}
                    >
                        {isMouseOverLock ? <LockOpen sx={{ fontSize: 14, p: 0, m: 0 }} /> : <Lock sx={{ fontSize: 14, p: 0, m: 0 }} />}
                    </Box>
                </Tooltip>
            }
            {/* Parked ask_user prompt: surfaced as a pane-scoped bottom sheet
                docked just above the composer while the turn waits on the user,
                so a blocked turn never reads as a finished one. */}
            <AskUserSheet promptHeight={promptHeight} />

            {/* Hosts that mount their own prompt below ChatPane (e.g.
                v2 Composer's DrawablePromptV2) pass `hidePromptInput`
                to suppress this internal Prompt — that way ChatPane is
                a pure transcript and we don't duplicate the chrome. */}
            {!hidePromptInput && (compactPrompt
                ? (
                    <PromptV2
                        onHeightChange={(newHeight) => setPromptHeight(() => newHeight)}
                        forceMessage={forceMessage}
                    />
                )
                : (
                    <Prompt
                        onHeightChange={(newHeight) => setPromptHeight(() => newHeight)}
                        forceMessage={forceMessage}
                    />
                ))}

            {/* Alert Dialog */}
            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                actionType={alertDialog.actionType}
                severity={alertDialog.severity}
                onClose={closeAlertAndRetry}
                onRetry={closeAlertAndRetry}
            />

            {/* {dashboardUrl && <DashboardModal open={!!dashboardUrl} id={dashboardUrl} onClose={() => setDashboardUrl(null)} />} */}
        </Box >


    )
}

export default ChatPane; 